/**
 * Pending Invitations Dropdown
 * 
 * Displays pending organization invitations for the current user
 * Allows users to accept or decline invitations
 */

import { useState } from 'react';
import { Bell, Check, X, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useOrganizationInvitations } from '@/hooks/useOrganizationInvitations';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';

export function PendingInvitationsDropdown() {
  const { pendingInvitations, acceptInvitation, cancelInvitation, loading } = useOrganizationInvitations();
  const { toast } = useToast();
  const [processingId, setProcessingId] = useState<string | null>(null);

  const handleAccept = async (invitationId: string) => {
    setProcessingId(invitationId);
    try {
      await acceptInvitation(invitationId);
      toast({
        title: "Invitation Accepted",
        description: "You've successfully joined the organization. Refreshing...",
      });
      // Refresh the page to update auth context
      setTimeout(() => window.location.reload(), 1000);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to accept invitation",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  const handleDecline = async (invitationId: string) => {
    setProcessingId(invitationId);
    try {
      await cancelInvitation(invitationId);
      toast({
        title: "Invitation Declined",
        description: "The invitation has been declined.",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to decline invitation",
        variant: "destructive",
      });
    } finally {
      setProcessingId(null);
    }
  };

  if (loading || !pendingInvitations || pendingInvitations.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Building2 className="h-5 w-5" />
          {pendingInvitations && pendingInvitations.length > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {pendingInvitations.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[380px]">
        <DropdownMenuLabel>Organization Invitations</DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {!pendingInvitations || pendingInvitations.length === 0 ? (
          <div className="p-4 text-center text-sm text-muted-foreground">
            No pending invitations
          </div>
        ) : (
          <div className="max-h-[400px] overflow-y-auto">
            {pendingInvitations.map((invitation) => (
              <div key={invitation.id} className="p-3 border-b last:border-b-0">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10">
                    <Building2 className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-sm truncate">
                        {invitation.organization_name}
                      </p>
                      <Badge variant="outline" className="text-xs">
                        {invitation.org_role}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      Invited {format(new Date(invitation.created_at), 'MMM d, yyyy')}
                    </p>
                    {invitation.personal_message && (
                      <p className="text-xs text-muted-foreground mb-3 italic">
                        "{invitation.personal_message}"
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => handleAccept(invitation.id)}
                        disabled={processingId === invitation.id}
                        className="flex-1"
                      >
                        <Check className="h-3 w-3 mr-1" />
                        Accept
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDecline(invitation.id)}
                        disabled={processingId === invitation.id}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
